/** @prettier */

import {useState, useEffect, useCallback, useMemo} from 'react';
import {Amplify, Auth} from 'aws-amplify';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {Backdrop, CircularProgress, Box} from '@mui/material';
import {
  Outlet,
  useLoaderData,
  useOutletContext,
  useNavigation,
  useLocation,
  useParams,
} from 'react-router-dom';
import * as coreLocales from '@mui/material/locale';
import * as dataGridLocales from '@mui/x-data-grid/locales';
import * as pickersLocales from '@mui/x-date-pickers/locales';

//API import
import {getSettingUser, getStatesErrorSites} from '../API/API_Collection';
//MTH component import
import {default as LoginHeader} from './LoginHeader';
import {default as MainHeader} from './MainHeader';
import Footer from './MainFooter';
//Style import
import '../css/common/common.css';
//interface import
import * as apiIF from '../Type/api';

import {color} from '../define';
import {sortByUnicode} from '../functions/sort';

type SupportedCoreLocales = keyof typeof coreLocales;
type SupportedDataGridLocales = keyof typeof dataGridLocales;
type SupportedPickersLocales = keyof typeof pickersLocales;

Amplify.configure({
  Auth: {
    // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
    identityPoolId: process.env.REACT_APP_identityPoolId,

    // REQUIRED - Amazon Cognito Region
    region: process.env.REACT_APP_cognitoRegion,

    // OPTIONAL - Amazon Cognito Federated Identity Pool Region
    // Required only if it's different from Amazon Cognito Region
    identityPoolRegion: process.env.REACT_APP_identityPoolRegion,

    // OPTIONAL - Amazon Cognito User Pool ID
    userPoolId: process.env.REACT_APP_userPoolId,

    // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
    userPoolWebClientId: process.env.REACT_APP_userPoolWebClientId,
  },
  Storage: {
    AWSS3: {
      bucket: process.env.REACT_APP_amplifyBucketName, //REQUIRED -  Amazon S3 bucket name
      region: process.env.REACT_APP_amplifyBucketRegion, //OPTIONAL -  Amazon service region
    },
  },
  API: {
    endpoints: [
      {
        name: 'my-api',
        endpoint: process.env.REACT_APP_apiUrl
          ? process.env.REACT_APP_apiUrl + process.env.REACT_APP_stage
          : '', //https://api.m-access.net/ft1
        region: process.env.REACT_APP_cognitoRegion,
      },
    ],
  },
});

//カスタムフック
type ContextType = {
  isAuthenticated: boolean;
  email: string;
  userInfo: apiIF.res.getSettingUser | null;
};
export function useUserState() {
  return useOutletContext() as ContextType;
}

/**
 * ユーザ情報の言語からMUI、Data Grid、Date and Time Pickersの翻訳用キーワードに変換
 * @param language ユーザ情報の言語
 * @returns 翻訳用キーワード
 */
const convertLanguageToLocale = (
  language: apiIF.res.getSettingUser['language']
): {
  core: SupportedCoreLocales;
  dataGrid: SupportedDataGridLocales;
  pickers: SupportedPickersLocales;
} => {
  switch (language) {
    case 'english':
      return {core: 'enUS', dataGrid: 'enUS', pickers: 'enUS'};
    case 'german':
      return {core: 'deDE', dataGrid: 'deDE', pickers: 'deDE'};
    case 'dutch':
      return {core: 'nlNL', dataGrid: 'nlNL', pickers: 'nlNL'};
    case 'italian':
      return {core: 'itIT', dataGrid: 'itIT', pickers: 'itIT'};
    case 'spanish':
      return {core: 'esES', dataGrid: 'esES', pickers: 'esES'};
    case 'french':
      return {core: 'frFR', dataGrid: 'frFR', pickers: 'frFR'};
    case 'greek':
      return {core: 'elGR', dataGrid: 'elGR', pickers: 'elGR'};
    case 'polish':
      return {core: 'plPL', dataGrid: 'plPL', pickers: 'plPL'};
    case 'portuguese':
      //@mui/x-date-pickers V6.14.0現在、翻訳されていないため英語で代用
      return {core: 'ptPT', dataGrid: 'ptPT', pickers: 'enUS'};
    case 'turkish':
      return {core: 'trTR', dataGrid: 'trTR', pickers: 'trTR'};
    default:
      return {core: 'enUS', dataGrid: 'enUS', pickers: 'enUS'};
  }
};

//認証状況、ユーザ情報を取得
type LoaderReturn = {
  isAuthenticated: boolean;
  email: string;
  userInfo: apiIF.res.getSettingUser | null;
  locale: {
    core: SupportedCoreLocales;
    dataGrid: SupportedDataGridLocales;
    pickers: SupportedPickersLocales;
  };
};
export async function loader(): Promise<LoaderReturn> {
  try {
    const promises = [];
    //認証状況を取得
    promises.push(Auth.currentAuthenticatedUser());
    //ユーザ情報を取得
    promises.push(getSettingUser());
    const result = await Promise.allSettled(promises);

    //認証に失敗した場合
    if (result[0].status === 'rejected') {
      return {
        isAuthenticated: false,
        email: '',
        userInfo: null,
        locale: convertLanguageToLocale('english'),
      };
    }
    //認証に成功した場合
    //ユーザ情報の取得に失敗した場合
    if (result[1].status === 'rejected') {
      return {
        isAuthenticated: true,
        email: result[0].value['attributes']['email'],
        userInfo: null,
        locale: convertLanguageToLocale('english'),
      };
    }
    //ユーザ情報の取得に成功した場合
    /** ユーザ情報 */
    const userInfo = result[1].value as apiIF.res.getSettingUser;
    return {
      isAuthenticated: true,
      email: result[0].value['attributes']['email'],
      userInfo: userInfo,
      locale: convertLanguageToLocale(userInfo.language),
    };
  } catch (error) {
    return {
      isAuthenticated: false,
      email: '',
      userInfo: null,
      locale: convertLanguageToLocale('english'),
    };
  }
}

const Root = (props: {isPopOut?: boolean}) => {
  const loaderData = useLoaderData() as LoaderReturn;
  const navigation = useNavigation();
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {target} = useParams();

  const [isHeaderShown, setIsHeaderShown] = useState(true); //ヘッダの有無
  const [lastPosition, setLastPosition] = useState(0); //前回のスクロール位置（y軸）
  //prettier-ignore
  const [errorState, setErrorState] = useState<apiIF.res.getStatesErrorSites['error-informations']>([]); //エラー状態値
  const [unitErrorNum, setUnitErrorNum] = useState(0); //エラー発生台数
  const [hccvqErrorTitle, setHCCVQErrorTitle] = useState(''); //HCCV(Q)エラー時にヘッダーに表示するタイトル
  const [airConditionerErrorTitle, setAirConditionerErrorTitle] = useState(''); //AirConditionerエラー時にヘッダーに表示するタイトル
  //prettier-ignore
  const [coreLocale, setCoreLocales] = useState<SupportedCoreLocales>(loaderData.locale.core); //MUIの翻訳言語
  //prettier-ignore
  const [dataGridLocale, setDataGridLocale] = useState<SupportedDataGridLocales>(loaderData.locale.dataGrid); //Data Gridの翻訳言語
  //prettier-ignore
  const [pickersLocale, setPickersLocale] = useState<SupportedPickersLocales>(loaderData.locale.pickers); //Date and Time Pickersの翻訳言語

  /** メインコンテンツの表示を切り替える変数 */
  const isAuthenticated = loaderData.isAuthenticated;
  /** ヘッダを消す位置 */
  const headerHeight = 100;
  /** MUIの基本テーマ */
  const theme = createTheme(
    {
      typography: {
        fontFamily: 'DINNextLTPro-Regular',
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: `
          @font-face {
            font-family: 'DINNextLTPro-Regular';
          }
        `,
        },
        MuiButton: {
          styleOverrides: {
            root: {
              fontFamily: 'DINNextLTPro-Bold',
              borderRadius: '12px',
            },
          },
        },
      },
      palette: {
        primary: {
          main: color.OBJECT_BASE_MHI_RED,
        },
        secondary: {
          main: color.OBJECT_BASE_PURE_BLACK,
        },
      },
    },
    coreLocales[coreLocale],
    dataGridLocales[dataGridLocale],
    pickersLocales[pickersLocale]
  );

  /**
   * スクロールイベントの検知
   */
  const scrollEvent = useCallback(() => {
    /** 現在位置 */
    const offset = window.scrollY;
    //間引き
    //大量に検知してしまうため、100px以内の移動は無視する
    if (Math.abs(lastPosition - offset) < 100) {
      return;
    }

    if (offset > headerHeight) {
      setIsHeaderShown(false);
    } else {
      setIsHeaderShown(true);
    }

    if (offset < lastPosition) {
      setIsHeaderShown(true);
    }

    setLastPosition(offset);
  }, [lastPosition]);
  useEffect(() => {
    window.addEventListener('scroll', scrollEvent);
    return () => {
      window.removeEventListener('scroll', scrollEvent);
    };
  }, [scrollEvent]);

  useEffect(() => {
    /**
     * HCCVQ、Air Conditionerエラー情報リクエスト
     */
    const reqErrorStates = () => {
      getStatesErrorSites()
        .then(response => {
          //サイト名でソート
          response['error-informations'].sort(sortByUnicode('site-name'));

          //ユニット名でソート
          response['error-informations'].forEach(siteInfo => {
            //空調機
            if (siteInfo['airconditioner'] !== undefined) {
              siteInfo['airconditioner'].sort(
                sortByUnicode('indoor-unit-name')
              );
            }

            //Hyozanコンデンシングユニット名
            if (
              siteInfo['hyozan'] !== undefined &&
              siteInfo['hyozan']['outdoor-unit-error-states'] !== undefined
            ) {
              siteInfo['hyozan']['outdoor-unit-error-states'].sort(
                sortByUnicode('outdoor-unit-name')
              );
            }
            //Hyozanクーラユニット名
            if (
              siteInfo['hyozan'] !== undefined &&
              siteInfo['hyozan']['indoor-unit-error-states'] !== undefined
            ) {
              siteInfo['hyozan']['indoor-unit-error-states'].sort(
                sortByUnicode('indoor-unit-name')
              );
            }

            //HCCVQコンデンシングユニット名
            if (
              siteInfo['hccvq'] !== undefined &&
              siteInfo['hccvq']['outdoor-unit-error-states'] !== undefined
            ) {
              siteInfo['hccvq']['outdoor-unit-error-states'].sort(
                sortByUnicode('outdoor-unit-name')
              );
            }
            //HCCVQクーラユニット名
            if (
              siteInfo['hccvq'] !== undefined &&
              siteInfo['hccvq']['indoor-unit-error-states'] !== undefined
            ) {
              siteInfo['hccvq']['indoor-unit-error-states'].sort(
                sortByUnicode('indoor-unit-name')
              );
            }
          });
          setErrorState(response['error-informations']);

          /** エラー発生台数 */
          let counter = 0;
          /** HCCV(Q)エラー時にヘッダーに表示するタイトル */
          let hccvqTitle = '';
          /** AirConditionerエラー時にヘッダーに表示するタイトル */
          let airConditionerTitle = '';
          if (response['error-informations'].length > 0) {
            //サイト単位で取得データを参照
            for (const siteState of response['error-informations']) {
              // Hyozan
              // 室外エラー(エラーコード)がある場合
              if (
                siteState.hyozan['outdoor-unit-error-states'] !== undefined &&
                siteState.hyozan['outdoor-unit-error-states'].length > 0
              ) {
                counter += siteState.hyozan['outdoor-unit-error-states'].length;
                //hyozan室内機のエラーが優先
                if (hccvqTitle === '') {
                  //prettier-ignore
                  hccvqTitle = siteState.hyozan['outdoor-unit-error-states'][0]['outdoor-unit-name'];
                }
              }
              //室内エラー(庫内温度異常)がある場合
              if (
                siteState.hyozan['indoor-unit-error-states'] !== undefined &&
                siteState.hyozan['indoor-unit-error-states'].length > 0
              ) {
                counter += siteState.hyozan['indoor-unit-error-states'].length;
                //室内機のエラーが優先
                hccvqTitle = 'iuError';
              }

              // HCCVQ
              // 室外エラー(エラーコード)がある場合
              if (
                siteState.hccvq['outdoor-unit-error-states'] !== undefined &&
                siteState.hccvq['outdoor-unit-error-states'].length > 0
              ) {
                counter += siteState.hccvq['outdoor-unit-error-states'].length;
                //HCCV(Q)室内機のエラーが優先
                if (hccvqTitle === '') {
                  //prettier-ignore
                  hccvqTitle = siteState.hccvq['outdoor-unit-error-states'][0]['outdoor-unit-name'];
                }
              }
              //室内エラー(庫内温度異常)がある場合
              if (
                siteState.hccvq['indoor-unit-error-states'] !== undefined &&
                siteState.hccvq['indoor-unit-error-states'].length > 0
              ) {
                counter += siteState.hccvq['indoor-unit-error-states'].length;
                //室内機のエラーが優先
                hccvqTitle = 'iuError';
              }

              //冷媒漏洩がある場合
              if (
                siteState.airconditioner !== undefined &&
                siteState.airconditioner.length > 0
              ) {
                counter += siteState.airconditioner.length;
                if (airConditionerTitle === '') {
                  //prettier-ignore
                  airConditionerTitle = 'airConditionerError';
                }
              }
            }
          }
          //エラー発生台数をセット
          setUnitErrorNum(counter);
          //HCCV(Q)エラー時にヘッダーに表示するタイトルをセット
          setHCCVQErrorTitle(hccvqTitle);
          //Air Conditionerエラー時にヘッダーに表示するタイトルをセット
          setAirConditionerErrorTitle(airConditionerTitle);
        })
        .catch(err => {});
    };
    //ページ遷移時初回読み込み
    reqErrorStates();
    //定期実行
    const intervalId = setInterval(() => {
      reqErrorStates();
    }, 60000);
    return function () {
      clearInterval(intervalId);
    };
  }, []);

  /** MUI、Data Grid、Date and Time Pickersの翻訳言語の変更処理 */
  const changeLocale = useMemo(
    () => ({
      toggleLocale: (language: apiIF.res.getSettingUser['language']) => {
        const locale = convertLanguageToLocale(language);
        setCoreLocales(locale.core);
        setDataGridLocale(locale.dataGrid);
        setPickersLocale(locale.pickers);
      },
    }),
    []
  );

  /**
   * 横幅いっぱいのフッターを表示するページかどうか判定
   * @param pathName パス
   * @param targetPathParameter targetパスパラメータ
   * @returns 判定結果
   */
  const shouldDisplayFullWidthFooter = (
    pathName: string,
    targetPathParameter: string | undefined
  ) => {
    return (
      //マイサイト
      !pathName.endsWith('my-site') &&
      //サイト詳細（サイトの状態値）
      !pathName.endsWith('state') &&
      //グループ詳細
      targetPathParameter !== 'group' &&
      //Q-ton詳細
      targetPathParameter !== 'qton' &&
      //Hyozan詳細
      targetPathParameter !== 'hyozan' &&
      //Chiller詳細
      targetPathParameter !== 'chiller' &&
      //Modbus詳細
      targetPathParameter !== 'modbus' &&
      //Modbusグループ詳細
      targetPathParameter !== 'modbus-group' &&
      //Hyozanグループ詳細
      targetPathParameter !== 'hyozan-group'
    );
  };

  return (
    <ThemeProvider theme={theme}>
      {/** loaderの読み込み中に表示する */}
      <Backdrop
        sx={{color: '#fff', zIndex: theme => theme.zIndex.drawer + 1}}
        open={navigation.state === 'loading'}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {/** メイン */}
      {props.isPopOut ? (
        <Box id="popOut">
          <Outlet
            context={{
              isAuthenticated: isAuthenticated,
              email: loaderData.email,
              userInfo: loaderData.userInfo,
            }}
          />
        </Box>
      ) : (
        <Box id={isAuthenticated ? 'loginAfter' : 'loginBefore'}>
          {unitErrorNum > 0 ? (
            <>
              {/** ヘッダー */}
              <header className="hideHeader--alwaysVisible">
                {isAuthenticated ? (
                  <MainHeader
                    email={loaderData.email}
                    language={
                      loaderData.userInfo === null
                        ? 'english'
                        : loaderData.userInfo.language
                    }
                    changeLocaleFunc={changeLocale.toggleLocale}
                    errorState={errorState}
                    unitErrorNum={unitErrorNum}
                    hccvqErrorTitle={hccvqErrorTitle}
                    airConditionerErrorTitle={airConditionerErrorTitle}
                  />
                ) : (
                  <LoginHeader />
                )}
              </header>
              {/** コンテンツ */}
              <main className="mainContentsError">
                <Outlet
                  context={{
                    isAuthenticated: isAuthenticated,
                    email: loaderData.email,
                    userInfo: loaderData.userInfo,
                  }}
                />
              </main>
            </>
          ) : (
            <>
              {/** ヘッダー */}
              <header
                className={
                  isHeaderShown ? 'hideHeader--fadeIn' : 'hideHeader--fadeOut'
                }>
                {isAuthenticated ? (
                  <MainHeader
                    email={loaderData.email}
                    language={
                      loaderData.userInfo === null
                        ? 'english'
                        : loaderData.userInfo.language
                    }
                    changeLocaleFunc={changeLocale.toggleLocale}
                  />
                ) : (
                  <LoginHeader />
                )}
              </header>
              {/** コンテンツ */}
              <main className="mainContents">
                <Outlet
                  context={{
                    isAuthenticated: isAuthenticated,
                    email: loaderData.email,
                    userInfo: loaderData.userInfo,
                  }}
                />
              </main>
            </>
          )}
          {/** フッター（ログイン前、状態値表示画面は各画面でフッターを作成する） */}
          {isAuthenticated &&
          shouldDisplayFullWidthFooter(location.pathname, target) ? (
            <Footer
              footerText={
                loaderData.userInfo === null
                  ? ''
                  : loaderData.userInfo['footer-text']
              }
            />
          ) : (
            <></>
          )}
        </Box>
      )}
    </ThemeProvider>
  );
};

export default Root;
